import { apiClient } from './client'

export interface WorkspaceContextRow {
  [key: string]: unknown
}

export interface WorkspaceContext {
  workspaceId: string
  recentArtifacts: WorkspaceContextRow[]
  openTasks: WorkspaceContextRow[]
  timeline: WorkspaceContextRow[]
  sourceGraph: WorkspaceContextRow[]
  artifactsCount: number
  openTasksCount: number
  timelineCount: number
  sourceGraphCount: number
  status?: string
  degraded?: boolean
  error?: string
}

function toRows(value: unknown): WorkspaceContextRow[] {
  return Array.isArray(value)
    ? value.filter((row): row is WorkspaceContextRow => Boolean(row && typeof row === 'object'))
    : []
}

function toNumber(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function mapWorkspaceContext(payload: Record<string, any>): WorkspaceContext {
  return {
    workspaceId: String(payload.workspace_id ?? payload.workspaceId ?? ''),
    recentArtifacts: toRows(payload.recent_artifacts ?? payload.recentArtifacts),
    openTasks: toRows(payload.open_tasks ?? payload.openTasks),
    timeline: toRows(payload.timeline),
    sourceGraph: toRows(payload.source_graph ?? payload.sourceGraph),
    artifactsCount: toNumber(payload.artifacts_count ?? payload.artifactsCount),
    openTasksCount: toNumber(payload.open_tasks_count ?? payload.openTasksCount),
    timelineCount: toNumber(payload.timeline_count ?? payload.timelineCount),
    sourceGraphCount: toNumber(payload.source_graph_count ?? payload.sourceGraphCount),
    status: payload.status ? String(payload.status) : undefined,
    degraded: Boolean(payload.degraded),
    error: payload.error ? String(payload.error) : undefined,
  }
}

class WorkspaceContextAPI {
  async getSessionWorkspaceContext(sessionId: string, limit = 20): Promise<WorkspaceContext> {
    const response = await apiClient.get<Record<string, any>>(
      `/space/workspace_context/${sessionId}`,
      {
        params: { limit },
      },
    )

    const payload =
      response && typeof response === 'object' && response.data && typeof response.data === 'object'
        ? response.data
        : response

    return mapWorkspaceContext(payload as Record<string, any>)
  }
}

export const workspaceContextAPI = new WorkspaceContextAPI()
